import React, { useState, useMemo } from "react";
import AdminLayout from "./AdminDashboardLayout";
import { useApiQuery } from "../../hooks/useApiQuery";
import { format } from "date-fns";
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import SyncIcon from '@mui/icons-material/Sync';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';

interface SystemHealthResponse {
  success: boolean;
  message: string;
  data: {
    serverApi: {
      latencyMs: number;
      uptimePercent: number;
      errorRatePercent: number;
      lastCheckAt: string;
    };
    jobsQueues: {
      totalJobs: number;
      pendingJobs: number;
      failedJobs: number;
      lastCronAt: string;
    };
    webhooks: {
      total: number;
      successful: number;
      failed: number;
      retrying: number;
    };
    alerts: {
      critical: number;
      warnings: number;
      info: number;
      lastAlertAt: string | null;
    };
    refreshedAt: string;
  };
}

interface CronJob {
  jobName: string;
  status: 'SUCCESS' | 'FAILED' | 'RUNNING';
  lastRunAt: string;
  durationMs: number;
  nextRunAt: string;
}

interface CronJobsResponse {
  success: boolean;
  message: string;
  data: CronJob[];
}

interface WebhookLog {
  id: number;
  provider: string;
  endpoint: string;
  httpMethod: string;
  eventType: string | null;
  headersJson: string;
  requestBody: string;
  processingStatus: 'SUCCESS' | 'FAILED' | 'PENDING' | 'RETRYING';
  responseHttpStatus: number;
  errorMessage: string | null;
  receivedAt: string;
  updatedAt: string;
  processedAt: string | null;
}

interface WebhookLogsResponse {
  success: boolean;
  message: string;
  data: {
    content: WebhookLog[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

interface FailedDelivery {
  id: string;
  type: 'webhook' | 'email' | 'sms';
  endpoint: string;
  attemptCount: number;
  lastAttempt: string;
  nextRetry: string;
  error: string;
}

interface FailedSubscription {
  id: string;
  email: string;
  reason: string;
  attemptedAt: string;
  status: 'failed' | 'retrying';
}

interface ExportLog {
  id: string;
  type: string;
  status: 'completed' | 'failed' | 'processing';
  requestedBy: string;
  createdAt: string;
  completedAt?: string;
  fileSize?: string;
}

const SystemHealth: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'cron' | 'webhooks'>('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [webhookPage, setWebhookPage] = useState(0);

  // API call for system health overview
  const { data: healthResponse, isLoading: healthLoading, refetch: refetchHealth } = useApiQuery<SystemHealthResponse>({
    queryKey: ["system-health-overview"],
    request: {
      url: "/admin/system-health/overview",
      method: "GET",
    },
  });

  // API call for cron jobs
  const { data: cronJobsResponse, isLoading: cronLoading } = useApiQuery<CronJobsResponse>({
    queryKey: ["cron-jobs"],
    request: {
      url: "/admin/system-health/cron-jobs",
      method: "GET",
    },
  });

  const { data: webhookLogsResponse, isLoading: webhookLoading } = useApiQuery<WebhookLogsResponse>({
    queryKey: ["webhook-logs", webhookPage],
    request: {
      url: `/admin/system-health/webhook-logs?page=${webhookPage}&size=10`,
      method: "GET",
    },
  });

  // const { data: failedDeliveries, isLoading: deliveryLoading } = useApiQuery<FailedDelivery[]>({
  //   queryKey: ["failed-deliveries"],
  //   request: {
  //     url: "/admin/failed-deliveries",
  //     method: "GET",
  //   },
  // });

  // const { data: failedSubscriptions, isLoading: subscriptionLoading } = useApiQuery<FailedSubscription[]>({
  //   queryKey: ["failed-subscriptions"],
  //   request: {
  //     url: "/admin/failed-subscriptions",
  //     method: "GET",
  //   },
  // });

  // const { data: exportLogs, isLoading: exportLoading } = useApiQuery<ExportLog[]>({
  //   queryKey: ["export-logs"],
  //   request: {
  //     url: "/admin/export-logs",
  //     method: "GET",
  //   },
  // });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetchHealth();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
      case 'sent':
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'running':
      case 'pending':
      case 'processing':
      case 'retrying':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
      case 'sent':
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'failed':
        return <ErrorIcon className="w-4 h-4" />;
      case 'running':
      case 'pending':
      case 'processing':
      case 'retrying':
        return <WarningIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const data = healthResponse?.data;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <MonitorHeartIcon className="w-4 h-4" /> },
    { id: 'cron', label: 'Cron Jobs', icon: <ScheduleIcon className="w-4 h-4" /> },
    { id: 'webhooks', label: 'Webhook logs', icon: <NotificationsActiveIcon className="w-4 h-4" /> },
    // { id: 'deliveries', label: 'Failed Deliveries', icon: <ErrorIcon className="w-4 h-4" /> },
    // { id: 'subscriptions', label: 'Failed Subscriptions', icon: <PersonIcon className="w-4 h-4" /> },
    // { id: 'exports', label: 'Export Logs', icon: <DescriptionIcon className="w-4 h-4" /> },
  ];

  return (
    <AdminLayout role="SYSTEM_ADMIN">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
            <p className="mt-1 text-sm text-gray-600">
              Monitor platform stability, resilience, and observability
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshIcon className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Server & API Monitoring */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Server & API</h3>
                <CloudQueueIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Latency</span>
                  <span className="text-sm font-medium">{data?.serverApi.latencyMs}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Uptime</span>
                  <span className="text-sm font-medium">{data?.serverApi.uptimePercent}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Error Rate</span>
                  <span className="text-sm font-medium">{data?.serverApi.errorRatePercent}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Check</span>
                  <span className="text-sm font-medium">
                    {data?.serverApi.lastCheckAt ? format(new Date(data.serverApi.lastCheckAt), 'HH:mm:ss') : '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* Job / Queue Monitoring */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Jobs & Queues</h3>
                <SyncIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Jobs</span>
                  <span className="text-sm font-medium">{data?.jobsQueues.totalJobs}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="text-sm font-medium text-yellow-600">{data?.jobsQueues.pendingJobs}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Failed</span>
                  <span className="text-sm font-medium text-red-600">{data?.jobsQueues.failedJobs}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Cron</span>
                  <span className="text-sm font-medium">
                    {data?.jobsQueues.lastCronAt ? format(new Date(data.jobsQueues.lastCronAt), 'HH:mm:ss') : '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* Webhook Monitoring */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Webhooks</h3>
                <NotificationsActiveIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total</span>
                  <span className="text-sm font-medium">{data?.webhooks.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Successful</span>
                  <span className="text-sm font-medium text-green-600">{data?.webhooks.successful}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Failed</span>
                  <span className="text-sm font-medium text-red-600">{data?.webhooks.failed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Retrying</span>
                  <span className="text-sm font-medium text-yellow-600">{data?.webhooks.retrying}</span>
                </div>
              </div>
            </div>

            {/* System Alerts */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">System Alerts</h3>
                <MonitorHeartIcon className="w-6 h-6 text-red-600" />
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Critical</span>
                  <span className="text-sm font-medium text-red-600">{data?.alerts.critical}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Warnings</span>
                  <span className="text-sm font-medium text-yellow-600">{data?.alerts.warnings}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Info</span>
                  <span className="text-sm font-medium text-blue-600">{data?.alerts.info}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Alert</span>
                  <span className="text-sm font-medium">
                    {data?.alerts.lastAlertAt ? format(new Date(data.alerts.lastAlertAt), 'HH:mm:ss') : '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cron Jobs Tab */}
        {activeTab === 'cron' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Run
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Next Run
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {cronLoading ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    cronJobsResponse?.data?.map((job, index) => (
                      <tr key={`${job.jobName}-${index}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {job.jobName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                            {getStatusIcon(job.status)}
                            <span className="ml-1">{job.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(job.lastRunAt), 'MMM dd, HH:mm:ss')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(job.durationMs / 1000).toFixed(1)}s
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(job.nextRunAt), 'MMM dd, HH:mm:ss')}
                        </td>
                      </tr>
                    )) || (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                          No cron jobs data available
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Webhook Logs Tab */}
        {activeTab === 'webhooks' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Endpoint
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Response Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Received At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Processed At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Error Message
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {webhookLoading ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    webhookLogsResponse?.data?.content?.map((log) => (
                      <tr key={log.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {log.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            {log.provider}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.endpoint}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                            log.httpMethod === 'POST' ? 'bg-green-100 text-green-800' : 
                            log.httpMethod === 'GET' ? 'bg-blue-100 text-blue-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {log.httpMethod}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.processingStatus)}`}>
                            {getStatusIcon(log.processingStatus)}
                            <span className="ml-1">{log.processingStatus}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                            log.responseHttpStatus >= 200 && log.responseHttpStatus < 300 ? 'bg-green-100 text-green-800' :
                            log.responseHttpStatus >= 400 ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {log.responseHttpStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(log.receivedAt), 'MMM dd, HH:mm:ss')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.processedAt ? format(new Date(log.processedAt), 'MMM dd, HH:mm:ss') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                          {log.errorMessage || '-'}
                        </td>
                      </tr>
                    )) || (
                      <tr>
                        <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                          No webhook logs available
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {webhookLogsResponse?.data && webhookLogsResponse.data.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setWebhookPage(Math.max(0, webhookPage - 1))}
                    disabled={webhookPage === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setWebhookPage(Math.min(webhookLogsResponse.data.totalPages - 1, webhookPage + 1))}
                    disabled={webhookPage >= webhookLogsResponse.data.totalPages - 1}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{webhookLogsResponse.data.pageNumber * webhookLogsResponse.data.pageSize + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min((webhookLogsResponse.data.pageNumber + 1) * webhookLogsResponse.data.pageSize, webhookLogsResponse.data.totalElements)}
                      </span>{' '}
                      of <span className="font-medium">{webhookLogsResponse.data.totalElements}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setWebhookPage(Math.max(0, webhookPage - 1))}
                        disabled={webhookPage === 0}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: webhookLogsResponse.data.totalPages }, (_, i) => i).map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => setWebhookPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === webhookPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => setWebhookPage(Math.min(webhookLogsResponse.data.totalPages - 1, webhookPage + 1))}
                        disabled={webhookPage >= webhookLogsResponse.data.totalPages - 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Failed Deliveries Tab */}
        {/* {activeTab === 'deliveries' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Endpoint
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attempts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Attempt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Next Retry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Error
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deliveryLoading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    // Mock failed deliveries data
                    [
                      { id: '1', type: 'webhook' as const, endpoint: 'https://api.example.com/webhook', attemptCount: 3, lastAttempt: new Date(Date.now() - 600000).toISOString(), nextRetry: new Date(Date.now() + 1800000).toISOString(), error: 'Connection timeout' },
                      { id: '2', type: 'email' as const, endpoint: 'user@example.com', attemptCount: 2, lastAttempt: new Date(Date.now() - 300000).toISOString(), nextRetry: new Date(Date.now() + 900000).toISOString(), error: 'SMTP server unavailable' },
                      { id: '3', type: 'sms' as const, endpoint: '+1234567890', attemptCount: 1, lastAttempt: new Date(Date.now() - 120000).toISOString(), nextRetry: new Date(Date.now() + 300000).toISOString(), error: 'Carrier rejected' },
                    ].map((delivery) => (
                      <tr key={delivery.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                            delivery.type === 'webhook' ? 'bg-purple-100 text-purple-800' : 
                            delivery.type === 'email' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {delivery.type.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {delivery.endpoint}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {delivery.attemptCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(delivery.lastAttempt), 'MMM dd, HH:mm:ss')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(delivery.nextRetry), 'MMM dd, HH:mm:ss')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                          {delivery.error}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )} */}

        {/* Failed Subscriptions Tab */}
        {/* {activeTab === 'subscriptions' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attempted At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscriptionLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    // Mock failed subscriptions data
                    [
                      { id: '1', email: 'invalid@example', reason: 'Invalid email format', attemptedAt: new Date(Date.now() - 600000).toISOString(), status: 'failed' as const },
                      { id: '2', email: 'blocked@example.com', reason: 'Email domain blocked', attemptedAt: new Date(Date.now() - 300000).toISOString(), status: 'retrying' as const },
                      { id: '3', email: 'duplicate@example.com', reason: 'Already subscribed', attemptedAt: new Date(Date.now() - 120000).toISOString(), status: 'failed' as const },
                    ].map((subscription) => (
                      <tr key={subscription.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {subscription.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {subscription.reason}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(subscription.attemptedAt), 'MMM dd, HH:mm:ss')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                            {getStatusIcon(subscription.status)}
                            <span className="ml-1">{subscription.status}</span>
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )} */}

       
      </div>
    </AdminLayout>
  );
};

export default SystemHealth;
